<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';

  let tenants = [];
  let selectedId = '';
  let loading = true;
  let pinning = false;
  let error = null;
  let identityChanged = false;
  let memberMismatch = false;

  onMount(async () => {
    const params = new URLSearchParams(window.location.search);
    identityChanged = params.get('identity_changed') === '1';
    memberMismatch = params.get('member_mismatch') === '1';
    try {
      const auth = await fetch('/api/user', { credentials: 'include' });
      if (!auth.ok) {
        navigate('/login');
        return;
      }
      const res = await fetch('/api/xero/available-tenants');
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not load Xero organisations');
      }
      tenants = data.tenants || [];
      if (tenants.length === 1) {
        selectedId = tenants[0].tenantId;
      }
      if (tenants.length === 0) {
        error = data.memberError
          || (memberMismatch
            ? "Your organisation's Xero organisation isn't available on this Xero login. Ask your organisation owner to invite you to it in Xero, then reconnect."
            : 'No Xero organisations are available on this login. Reconnect to Xero and choose an organisation.');
      }
    } catch (err) {
      error = err.message || 'Failed to load Xero organisations';
    } finally {
      loading = false;
    }
  });

  async function pinSelected() {
    if (!selectedId || pinning) return;
    pinning = true;
    error = null;
    try {
      const res = await fetch('/api/xero/pin-tenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedId })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not pin this organisation');
      }
      const qs = identityChanged ? '&identity_changed=1' : '';
      window.location.href = `/settings/xero?setup=required${qs}`;
    } catch (err) {
      error = err.message || 'Failed to pin organisation';
      pinning = false;
    }
  }
</script>

<div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
  <div class="bg-white w-full max-w-lg rounded-xl shadow-lg p-6">
    <h1 class="text-xl font-bold text-gray-900 mb-1">Choose your Xero organisation</h1>
    <p class="text-sm text-gray-600 mb-5">
      This Buildzilla account will stay pinned to one Xero organisation. You can change it later by disconnecting and reconnecting.
    </p>

    {#if identityChanged}
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded text-sm">
        You signed into Xero as a different user. Pick the organisation this Buildzilla account should use.
      </div>
    {/if}

    {#if loading}
      <div class="flex items-center py-8 text-sm text-gray-600">
        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
        Loading organisations…
      </div>
    {:else if error && tenants.length === 0}
      <div class="p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm mb-4">{error}</div>
      <a href="/api/xero/login?prompt=login" class="text-sm text-blue-600 underline">Reconnect to Xero</a>
    {:else}
      {#if error}
        <div class="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
      {/if}
      <div class="space-y-2 mb-6">
        {#each tenants as t}
          <label
            class="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer
              {selectedId === t.tenantId ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}"
          >
            <input type="radio" name="xero-org" value={t.tenantId} bind:group={selectedId} class="text-blue-600" />
            <span class="text-sm font-medium text-gray-900">{t.tenantName || t.tenantId}</span>
          </label>
        {/each}
      </div>
      <button
        type="button"
        on:click={pinSelected}
        disabled={!selectedId || pinning}
        class="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed border-0"
      >
        {#if pinning}Saving…{:else}Use this organisation{/if}
      </button>
    {/if}
  </div>
</div>
